import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.removeEventListener('click', this.#handleClick);
  }

  /**
   * Forwards cart-drawer-open clicks to the remote cart-drawer-component
   * (which lives in the cart-drawer-bundle section, not the header).
   * Closes the mobile menu drawer first when it is open so scroll lock / header state stay consistent.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const trigger = /** @type {Element | null} */ (event.target)?.closest('[data-cart-drawer-open]');
    if (!trigger) return;

    const mobileMenu = document.querySelector('header-drawer');
    if (mobileMenu instanceof HTMLElement) {
      const host = /** @type {{ isOpen?: boolean; close?: () => void }} */ (
        /** @type {unknown} */ (mobileMenu)
      );
      if (host.isOpen === true && typeof host.close === 'function') {
        host.close();
      }
    }

    /** @type {any} */
    const drawer = document.querySelector('cart-drawer-component');
    drawer?.open();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
